import type React from 'react'
import { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { Select, Avatar } from 'antd'
import { Horizontal } from '@gravitate-js/excalibrr'
import type { SupplierConfig } from '@api/useSupplierReconciliation'
interface User {
  username: string // Always present
  name?: string // Optional
  email?: string // Optional
}

interface UserSelectEditorProps {
  value?: string
  data: SupplierConfig
  context?: {
    users?: User[]
    onAssignUser?: (supplierId: string, userId: string | null) => Promise<void>
  }
}

function getInitials(name: string) {
  if (!name) return ''
  return name
    .split(' ')
    .map((part) => part?.[0] || '')
    .join('')
    .toUpperCase()
}

function UserSelectEditor(props: UserSelectEditorProps, ref: React.ForwardedRef<any>) {
  const [value, setValue] = useState<string | null>(props.value || null)

  useImperativeHandle(ref, () => {
    return {
      getValue() {
        return value
      },
      isPopup() {
        return true
      },
      isCancelBeforeStart() {
        return false
      },
      isCancelAfterEnd() {
        return false
      },
    }
  })

  const handleChange = async (newValue: string | null | undefined) => {
    // Convert undefined to null for consistency
    const normalizedValue = newValue === undefined ? null : newValue
    setValue(normalizedValue)
  }

  const users = props.context?.users || []

  // Deduplicate users by username to prevent duplicates in dropdown
  const uniqueUsers = users.reduce((acc: User[], user) => {
    if (!acc.find((u) => u.username === user.username)) {
      acc.push(user)
    }
    return acc
  }, [])

  const options = uniqueUsers
    .map((user) => {
      const displayName = user.name || user.username || user.email || ''

      return {
        value: user.username,
        label: (
          <Horizontal gap={8} alignItems='center'>
            <Avatar size='small' style={{ backgroundColor: '#1890ff' }}>
              {getInitials(displayName)}
            </Avatar>
            <span>{displayName}</span>
          </Horizontal>
        ),
        // Store sortKey and searchText for sorting and filtering
        sortKey: displayName,
        searchText: displayName.toLowerCase(),
      }
    })
    .sort((a, b) => a.sortKey.localeCompare(b.sortKey))

  return (
    <Select
      style={{ width: '100%', minWidth: 200 }}
      value={value}
      onChange={handleChange}
      options={options}
      allowClear
      showSearch
      autoFocus
      defaultOpen
      optionFilterProp='searchText'
      placeholder='Select user'
      filterOption={(input, option) => option?.searchText.includes(input.toLowerCase()) ?? false}
    />
  )
}

const ForwardedUserSelectEditor = forwardRef(UserSelectEditor)
export default ForwardedUserSelectEditor
